import { Suspense, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Clock, HardDrive, Plus, Settings, Trash2 } from 'lucide-react';
import {
  MAX_CUSTOM_SANDBOX_SIZES,
  MIN_CUSTOM_SANDBOX_CPU_CORES,
  MIN_CUSTOM_SANDBOX_DISK_GB,
  MIN_CUSTOM_SANDBOX_GB_MEMORY,
  customSandboxSizeLimitViolation,
  isBuiltinResourceSize,
  type CustomSandboxSize,
  type CustomSandboxSizeLimits,
} from '@runloop/reflex-contract';
import {
  DEFAULT_SANDBOX_RESOURCE_SIZE,
  SANDBOX_RESOURCE_SIZE_OPTIONS,
  isSubscriptionActive,
  type Organization,
} from '@reflex/shared';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@reflex/ui/components/ui/select';
import { Separator } from '@reflex/ui/components/ui/separator';
import { Switch } from '@reflex/ui/components/ui/switch';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { formatDateLong, formatRelativeTime } from '@reflex/ui/lib/format';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { HubReadGuard } from '@reflex/ui/components/layout/NoReadAccessState';
import { DirtyActionBar } from '@reflex/ui/components/shared/DirtyActionBar';
import { EntityDetailsCard } from '@/components/shared/EntityDetailsCard';
import { RequireActiveOrganization } from '@/components/shared/RequireActiveOrganization';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { usePlugins } from '@/hooks/config/usePlugins';
import { useCustomSandboxSizeLimits } from '@/hooks/config/useConfig';
import { api } from '@/lib/api';
import { mutateWithToast } from '@/lib/mutation-helpers';
import {
  DEFAULT_SANDBOX_RETENTION_DAYS,
  MIN_SANDBOX_RETENTION_DAYS,
  MAX_SANDBOX_RETENTION_DAYS,
} from '@/lib/types';

/** Select sentinel for "no org default; use the platform default size". */
const PLATFORM_DEFAULT_SIZE = '__platform__';

/** One editable row of the custom-size list, held as strings while typing. */
interface CustomSizeDraft {
  name: string;
  cpuCores: string;
  gbMemory: string;
  diskSizeGb: string;
}

function toDraft(size: CustomSandboxSize): CustomSizeDraft {
  return {
    name: size.name,
    cpuCores: String(size.cpuCores),
    gbMemory: String(size.gbMemory),
    diskSizeGb: size.diskSizeGb != null ? String(size.diskSizeGb) : '',
  };
}

/**
 * Parse the draft rows back into `CustomSandboxSize`s, or return the first
 * validation problem as a human-readable message. Mirrors the server-side
 * bounds so a bad row fails inline instead of as a request error.
 */
function parseDrafts(
  drafts: CustomSizeDraft[],
  limits: CustomSandboxSizeLimits,
): { sizes: CustomSandboxSize[] } | { error: string } {
  const sizes: CustomSandboxSize[] = [];
  const seen = new Set<string>();
  for (const draft of drafts) {
    const name = draft.name.trim();
    if (!name) return { error: 'Every custom size needs a name.' };
    if (isBuiltinResourceSize(name.toUpperCase())) {
      return { error: `"${name}" is a built-in size name. Pick a different name.` };
    }
    if (seen.has(name.toLowerCase())) {
      return { error: `Duplicate custom size name "${name}".` };
    }
    seen.add(name.toLowerCase());
    const cpuCores = Number(draft.cpuCores);
    if (!Number.isFinite(cpuCores) || cpuCores < MIN_CUSTOM_SANDBOX_CPU_CORES) {
      return {
        error: `"${name}": vCPU must be between ${MIN_CUSTOM_SANDBOX_CPU_CORES} and ${limits.maxCpuCores}.`,
      };
    }
    const gbMemory = Number(draft.gbMemory);
    if (!Number.isInteger(gbMemory) || gbMemory < MIN_CUSTOM_SANDBOX_GB_MEMORY) {
      return {
        error: `"${name}": memory must be a whole number between ${MIN_CUSTOM_SANDBOX_GB_MEMORY} and ${limits.maxGbMemory} GB.`,
      };
    }
    const disk = draft.diskSizeGb.trim();
    let diskSizeGb: number | null = null;
    if (disk !== '') {
      diskSizeGb = Number(disk);
      if (!Number.isInteger(diskSizeGb) || diskSizeGb < MIN_CUSTOM_SANDBOX_DISK_GB) {
        return {
          error: `"${name}": disk must be a whole number between ${MIN_CUSTOM_SANDBOX_DISK_GB} and ${limits.maxDiskSizeGb} GB, or left empty.`,
        };
      }
    }
    const size: CustomSandboxSize = {
      name,
      cpuCores,
      gbMemory,
      ...(diskSizeGb != null ? { diskSizeGb } : {}),
    };
    // Maximums come from the deployment's env-configured limits so accounts
    // provisioned above the documented ceilings can use their full quota.
    const violation = customSandboxSizeLimitViolation(size, limits);
    if (violation) return { error: violation };
    sizes.push(size);
  }
  return { sizes };
}

/**
 * A labeled block within a settings card: an uppercase heading, an optional
 * one-line description, and its controls. Gives the merged Sandboxes card a
 * scannable hierarchy so "Default size", "Custom sizes", and "Retention" read
 * as distinct groups without each needing its own card.
 */
function SettingsGroup({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <SectionLabel as="h3" tone="strong">
          {title}
        </SectionLabel>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      {children}
    </section>
  );
}

/**
 * Unified org sandbox settings: the default and custom sizes agents launch
 * with, plus how long idle sandboxes are retained. All three live in one card
 * and persist through a single Save so a member reviews every sandbox policy in
 * one place. The three groups validate independently but share one dirty state.
 */
function SandboxesCard({ org, canManage }: { org: Organization; canManage: boolean }) {
  const { refreshSession } = useAuth();
  const limits = useCustomSandboxSizeLimits();

  // --- Sizes ---
  const savedSizes = org.customSandboxSizes ?? [];
  const savedDefault = org.defaultSandboxSize ?? null;
  const [drafts, setDrafts] = useState<CustomSizeDraft[]>(savedSizes.map(toDraft));
  const [defaultSize, setDefaultSize] = useState<string>(savedDefault ?? PLATFORM_DEFAULT_SIZE);

  // --- Retention ---
  // `null` means "retain forever"; `undefined` (legacy orgs) falls back to the
  // default window. Keep the two distinct so the toggle reflects a saved
  // "indefinite" choice instead of snapping back to the default.
  const savedRetention = org.sandboxRetentionDays;
  const currentRetention: number | null =
    savedRetention === undefined ? DEFAULT_SANDBOX_RETENTION_DAYS : savedRetention;
  const [indefinite, setIndefinite] = useState(currentRetention === null);
  const [retention, setRetention] = useState(
    String(currentRetention ?? DEFAULT_SANDBOX_RETENTION_DAYS),
  );

  const updateMutation = useMutation({
    mutationFn: (data: {
      defaultSandboxSize?: string | null;
      customSandboxSizes?: CustomSandboxSize[];
      sandboxRetentionDays?: number | null;
    }) => api.updateOrganization(org.id, data),
    onSuccess: async () => {
      // The org's default/custom sandbox sizes live on the org object in the
      // `useAuth` reducer (fed by /auth/me), not in a React Query cache, so
      // the launcher's size chip reads stale values until the session is
      // re-fetched. Refresh the session so the change shows without a reload.
      await refreshSession();
    },
  });

  const parsed = parseDrafts(drafts, limits);
  const parseError = 'error' in parsed ? parsed.error : null;
  const nextSizes = 'sizes' in parsed ? parsed.sizes : null;
  const nextDefault = defaultSize === PLATFORM_DEFAULT_SIZE ? null : defaultSize;
  // A default that names a custom size must still exist in the draft list.
  const defaultValid =
    nextDefault === null ||
    isBuiltinResourceSize(nextDefault) ||
    (nextSizes?.some((size) => size.name === nextDefault) ?? false);
  const sizesValid = nextSizes != null && defaultValid;
  const sizesDirty =
    nextSizes != null &&
    (nextDefault !== savedDefault || JSON.stringify(nextSizes) !== JSON.stringify(savedSizes));

  const retentionValue = Number(retention);
  const retentionValid =
    indefinite ||
    (Number.isInteger(retentionValue) &&
      retentionValue >= MIN_SANDBOX_RETENTION_DAYS &&
      retentionValue <= MAX_SANDBOX_RETENTION_DAYS);
  const nextRetention: number | null = indefinite ? null : retentionValue;
  const retentionDirty = retentionValid && nextRetention !== currentRetention;

  const dirty = sizesDirty || retentionDirty;
  // Only block Save on a group the member actually changed, so a bad custom
  // size can't trap an unrelated retention edit (and vice versa).
  const valid = (!sizesDirty || sizesValid) && (!retentionDirty || retentionValid);

  const platformDefaultLabel =
    SANDBOX_RESOURCE_SIZE_OPTIONS.find((o) => o.value === DEFAULT_SANDBOX_RESOURCE_SIZE)?.label ??
    DEFAULT_SANDBOX_RESOURCE_SIZE;

  function updateDraft(index: number, patch: Partial<CustomSizeDraft>) {
    setDrafts((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function removeDraft(index: number) {
    const removed = drafts[index];
    setDrafts((prev) => prev.filter((_, i) => i !== index));
    // Removing the size the default points at silently reverts the default
    // to the platform tier so Save stays possible.
    if (removed && defaultSize === removed.name.trim()) {
      setDefaultSize(PLATFORM_DEFAULT_SIZE);
    }
  }

  function handleReset() {
    setDrafts(savedSizes.map(toDraft));
    setDefaultSize(savedDefault ?? PLATFORM_DEFAULT_SIZE);
    setIndefinite(currentRetention === null);
    setRetention(String(currentRetention ?? DEFAULT_SANDBOX_RETENTION_DAYS));
  }

  function handleSave() {
    if (!dirty || !valid) return;
    const updates: {
      defaultSandboxSize?: string | null;
      customSandboxSizes?: CustomSandboxSize[];
      sandboxRetentionDays?: number | null;
    } = {};
    if (sizesDirty && nextSizes) {
      updates.defaultSandboxSize = nextDefault;
      updates.customSandboxSizes = nextSizes;
    }
    if (retentionDirty) updates.sandboxRetentionDays = nextRetention;
    mutateWithToast(updateMutation, updates, {
      success: 'Sandbox settings updated',
      error: 'Failed to update sandbox settings',
    });
  }

  return (
    <SectionCard
      title="Sandboxes"
      description="Control the size and lifetime of the sandboxes your agents run in."
      icon={<HardDrive />}
      data-testid="org-sandboxes-section"
    >
      <div className="flex flex-col gap-6">
        <SettingsGroup
          title="Default size"
          description="Applied when a launch leaves the sandbox size unset."
        >
          <div className="max-w-xs">
            <Label htmlFor="org-default-sandbox-size" className="sr-only">
              Default size
            </Label>
            <Select value={defaultSize} onValueChange={setDefaultSize} disabled={!canManage}>
              <SelectTrigger id="org-default-sandbox-size" aria-label="Default sandbox size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PLATFORM_DEFAULT_SIZE}>
                  Platform default ({platformDefaultLabel})
                </SelectItem>
                {SANDBOX_RESOURCE_SIZE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label} ({opt.description})
                  </SelectItem>
                ))}
                {drafts
                  .map((draft) => draft.name.trim())
                  .filter((name) => name !== '' && !isBuiltinResourceSize(name.toUpperCase()))
                  .map((name) => (
                    <SelectItem key={`custom-${name}`} value={name}>
                      {name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </SettingsGroup>

        <Separator />

        <SettingsGroup
          title="Custom sizes"
          description="Extra sizes members can pick from in the launcher."
        >
          {drafts.length === 0 ? (
            <p className="text-xs text-muted-foreground">No custom sizes yet.</p>
          ) : (
            <ul className="flex flex-col gap-2" data-testid="org-custom-size-list">
              <li className="flex items-end gap-2 text-2xs text-muted-foreground" aria-hidden>
                <span className="flex-1">Name</span>
                <span className="w-20">vCPU</span>
                <span className="w-24">Memory (GB)</span>
                <span className="w-24">Disk (GB)</span>
                {canManage && <span className="w-9" />}
              </li>
              {drafts.map((draft, index) => (
                <li key={index} className="flex items-end gap-2" data-testid="org-custom-size-row">
                  <Input
                    className="flex-1"
                    value={draft.name}
                    disabled={!canManage}
                    placeholder="e.g. builder-xl"
                    aria-label={`Custom size ${index + 1} name`}
                    onChange={(e) => updateDraft(index, { name: e.target.value })}
                  />
                  <Input
                    className="w-20"
                    type="number"
                    min={MIN_CUSTOM_SANDBOX_CPU_CORES}
                    max={limits.maxCpuCores}
                    step={0.5}
                    value={draft.cpuCores}
                    disabled={!canManage}
                    aria-label={`Custom size ${index + 1} vCPU`}
                    onChange={(e) => updateDraft(index, { cpuCores: e.target.value })}
                  />
                  <Input
                    className="w-24"
                    type="number"
                    min={MIN_CUSTOM_SANDBOX_GB_MEMORY}
                    max={limits.maxGbMemory}
                    value={draft.gbMemory}
                    disabled={!canManage}
                    aria-label={`Custom size ${index + 1} memory`}
                    onChange={(e) => updateDraft(index, { gbMemory: e.target.value })}
                  />
                  <Input
                    className="w-24"
                    type="number"
                    min={MIN_CUSTOM_SANDBOX_DISK_GB}
                    max={limits.maxDiskSizeGb}
                    value={draft.diskSizeGb}
                    disabled={!canManage}
                    placeholder="16"
                    aria-label={`Custom size ${index + 1} disk`}
                    onChange={(e) => updateDraft(index, { diskSizeGb: e.target.value })}
                  />
                  {canManage && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive"
                      aria-label={`Remove custom size ${index + 1}`}
                      onClick={() => removeDraft(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canManage && (
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={drafts.length >= MAX_CUSTOM_SANDBOX_SIZES}
                onClick={() =>
                  setDrafts((prev) => [
                    ...prev,
                    { name: '', cpuCores: '4', gbMemory: '16', diskSizeGb: '' },
                  ])
                }
              >
                <Plus className="h-4 w-4" />
                Add custom size
              </Button>
            </div>
          )}
          {parseError && <p className="text-xs text-destructive">{parseError}</p>}
          {!parseError && !defaultValid && (
            <p className="text-xs text-destructive">
              The default size must be a built-in size or one of the custom sizes above.
            </p>
          )}
        </SettingsGroup>

        <Separator />

        <SettingsGroup
          title="Retention"
          description="An idle sandbox is kept so you can re-open and continue. After this many days with no activity it is cleaned up and can no longer be used."
        >
          <div className="flex items-center justify-between gap-4 max-w-xs">
            <Label htmlFor="org-sandbox-retention-indefinite">Keep sandboxes indefinitely</Label>
            <Switch
              id="org-sandbox-retention-indefinite"
              checked={indefinite}
              disabled={!canManage}
              onCheckedChange={setIndefinite}
              aria-label="Keep sandboxes indefinitely"
            />
          </div>
          <div className="flex flex-col gap-2 max-w-xs">
            <Label htmlFor="org-sandbox-retention-days">Keep sandboxes for (days)</Label>
            <Input
              id="org-sandbox-retention-days"
              type="number"
              min={MIN_SANDBOX_RETENTION_DAYS}
              max={MAX_SANDBOX_RETENTION_DAYS}
              value={retention}
              disabled={!canManage || indefinite}
              onChange={(e) => setRetention(e.target.value)}
              aria-label="Sandbox retention days"
            />
            {indefinite ? (
              <p className="text-xs text-muted-foreground">
                Sandboxes are kept indefinitely and are never cleaned up automatically.
              </p>
            ) : (
              !retentionValid && (
                <p className="text-xs text-destructive">
                  Enter a whole number of days between {MIN_SANDBOX_RETENTION_DAYS} and{' '}
                  {MAX_SANDBOX_RETENTION_DAYS}.
                </p>
              )
            )}
          </div>
        </SettingsGroup>

        {/* TODO: re-enable the "Package mirrors" toggle (org.sandboxArtifactMirrors)
            once the devbox mirror issues are resolved. Mirrors stay on by default
            server-side; this pane just no longer exposes the opt-out. */}

        {canManage && (
          <>
            <Separator />
            <DirtyActionBar
              dirty={dirty && valid}
              saving={updateMutation.isPending}
              onDiscard={handleReset}
              onSave={handleSave}
              permission="org:manage"
            />
          </>
        )}
      </div>
    </SectionCard>
  );
}

/**
 * Read-only summary of an org's subscription window. Rendered only for orgs
 * with a subscription; a regular org has nothing to show. The "Ends" row
 * pairs the absolute date with a relative phrase ("in 5 days" / "2 days ago")
 * via the shared `format*` helpers, and flips to "Ended" once the window has
 * passed.
 */
function TrialDetailsCard({ org }: { org: Organization }) {
  // Render for any org that carries a subscription — including an expired one,
  // so the member can still see when it ran.
  if (!org.subscription) return null;
  // Ended once the window passes; access is then blocked with a computed
  // disabledReason of 'subscription_expired'.
  const ended = !isSubscriptionActive(org.subscription);
  return (
    <SectionCard
      title="Trial"
      description="This organization is on a time-limited trial. Contact support to extend it or convert it to a regular organization."
      icon={<Clock />}
      contentClassName="pt-0"
    >
      <dl
        className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2"
        data-testid="org-trial-details"
      >
        <div>
          <SectionLabel as="dt">Status</SectionLabel>
          <dd className="mt-0.5">
            {ended ? (
              <Badge variant="outline" data-testid="org-trial-status">
                Ended
              </Badge>
            ) : (
              <Badge variant="secondary" data-testid="org-trial-status">
                Active
              </Badge>
            )}
          </dd>
        </div>
        <div>
          <SectionLabel as="dt">{ended ? 'Ended' : 'Ends'}</SectionLabel>
          <dd className="mt-0.5">
            {`${formatDateLong(org.subscription.endsAt)} (${formatRelativeTime(org.subscription.endsAt)})`}
          </dd>
        </div>
        <div>
          <SectionLabel as="dt">Started</SectionLabel>
          <dd className="mt-0.5">{formatDateLong(org.subscription.startsAt)}</dd>
        </div>
      </dl>
    </SectionCard>
  );
}

export function OrganizationSettingsPage({ embedded = false }: { embedded?: boolean }) {
  return (
    <HubReadGuard permission="org:read">
      <RequireActiveOrganization title="Organization">
        {(org) => <OrganizationSettingsForm key={org.id} embedded={embedded} />}
      </RequireActiveOrganization>
    </HubReadGuard>
  );
}

/**
 * Per-org plugin settings, one `SectionCard` per installed plugin that
 * contributes an `orgSettingsSections` entry. The server strips the
 * surface from uninstalled plugins' manifests, so an empty list means
 * nothing renders at all. Write controls inside each section are the
 * section component's responsibility (gated on `can('plugins:manage')`).
 */
function PluginSettingsSections() {
  const { orgSettingsSections } = usePlugins();
  if (orgSettingsSections.length === 0) return null;
  return (
    <>
      {orgSettingsSections.map((section) => {
        const SectionIcon = section.icon;
        return (
          <div key={section.id} id={section.id} className="scroll-mt-24">
            <SectionCard
              title={section.label}
              icon={<SectionIcon />}
              data-testid={`org-plugin-settings-${section.id}`}
            >
              <Suspense fallback={<div className="text-sm text-muted-foreground">Loading…</div>}>
                <section.component />
              </Suspense>
            </SectionCard>
          </div>
        );
      })}
    </>
  );
}

function OrganizationSettingsForm({ embedded = false }: { embedded?: boolean }) {
  const { activeOrganization, can } = useOrganization();
  const { refreshSession } = useAuth();
  const [name, setName] = useState(activeOrganization?.name ?? '');
  const [slug, setSlug] = useState(activeOrganization?.slug ?? '');

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; slug?: string }) => {
      if (!activeOrganization) throw new Error('No active organization');
      return api.updateOrganization(activeOrganization.id, data);
    },
    onSuccess: async () => {
      // Org profile lives in the `useAuth` reducer (fed by /auth/me), not in
      // a React Query cache, so name/slug/retention edits only surface across
      // the app after the session is re-fetched.
      await refreshSession();
    },
  });

  if (!activeOrganization) return null;

  const canManage = can('org:manage');
  const dirty = name !== activeOrganization.name || slug !== activeOrganization.slug;

  function handleSave() {
    if (!activeOrganization) return;
    const updates: { name?: string; slug?: string } = {};
    if (name !== activeOrganization.name) updates.name = name;
    if (slug !== activeOrganization.slug) updates.slug = slug;
    if (Object.keys(updates).length === 0) return;
    mutateWithToast(updateMutation, updates, {
      success: 'Organization updated',
      error: 'Failed to update organization',
    });
  }

  function handleReset() {
    if (!activeOrganization) return;
    setName(activeOrganization.name);
    setSlug(activeOrganization.slug);
  }
  const content = (
    <div className="mx-auto flex w-full max-w-6xl">
      <div className="min-w-0 flex-1">
        {!embedded && (
          <PageHeader
            iconVariant="box"
            icon={<Settings className="h-5 w-5 text-muted-foreground" />}
            title={activeOrganization.name}
            description="General settings for your organization."
            docs="administration/organization-teams#organizations-settings-grid"
          />
        )}

        <div className={embedded ? 'space-y-8' : 'mt-6 space-y-8'}>
          <div id="general" className="scroll-mt-24">
            <EntityDetailsCard
              title="General"
              description="Name and URL slug used across the platform."
              icon={<Settings />}
              data-testid="org-general-section"
              disabled={!canManage}
              name={{
                id: 'org-name',
                label: 'Name',
                value: name,
                onChange: setName,
              }}
              identifier={{
                id: 'org-slug',
                label: 'URL slug',
                value: slug,
                onChange: setSlug,
                hint: `Used in the organization URL (e.g. /${slug || 'acme'}).`,
              }}
              dirty={dirty}
              saving={updateMutation.isPending}
              onDiscard={handleReset}
              onSave={handleSave}
              savePermission="org:manage"
            />
          </div>

          {activeOrganization.subscription && (
            <div id="trial" className="scroll-mt-24">
              <TrialDetailsCard org={activeOrganization} />
            </div>
          )}

          <div id="sandboxes" className="scroll-mt-24">
            <SandboxesCard org={activeOrganization} canManage={canManage} />
          </div>

          {/*
           * The company's OIDC connection used to render here. It is a
           * company-wide setting, not an organization one, so it now lives on
           * Configure → Company → Security next to the per-org trust it pairs
           * with. Nothing organization-scoped moved.
           */}
          <PluginSettingsSections />
        </div>
      </div>
    </div>
  );

  if (embedded) return content;

  return <PageLayout maxWidth="full">{content}</PageLayout>;
}
